import { Clock, ExternalLink, Share2, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSite } from '../context/SiteContext';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductCard = ({ coupon: product, onUpdate, recommended = false }) => {
  const { settings, getImageUrl, darkMode } = useSite();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const expirationDate = new Date(product.expiration_date);
  const isExpired = expirationDate < new Date();

  const bgColor = darkMode
    ? (settings.dark_card_bg_color || '#1e293b')
    : (settings.default_coupon_bg_color || '#ffffff');
  const discountBg = settings.default_coupon_discount_bg || '#4f46e5';
  const buttonBg = settings.default_coupon_button_bg || '#fbbf24';
  const buttonText = settings.default_coupon_button_text || '#1e293b';
  const textColor = darkMode
    ? (settings.dark_text_color || '#f1f5f9')
    : (settings.default_coupon_text_color || '#1e293b');
  // Neumorphic palette (matches CouponCard)
  const cardBg = darkMode ? bgColor : '#ffffff';
  const cardBgImage = darkMode ? 'none' : 'linear-gradient(180deg, #ffffff 0%, #eaf1ff 100%)';
  const cardBorder = darkMode ? 'rgba(148,163,184,0.16)' : '#e3e9f5';
  const cardShadow = darkMode
    ? '0 4px 16px rgba(0,0,0,0.45)'
    : '0 2px 6px rgba(37,99,235,0.10), 0 12px 26px rgba(37,99,235,0.16)';

  useEffect(() => {
    const calc = () => {
      const diff = expirationDate - new Date();
      if (diff <= 0) {
        setTimeRemaining(null);
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining({ days, hours, minutes, seconds, totalMs: diff });
    };
    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [product.expiration_date]);

  const isExpiringSoon = timeRemaining && timeRemaining.totalMs < 60 * 60 * 1000;

  // Compact time formatter: shows only the 2 most significant units
  const formatCompactTime = (t) => {
    if (!t) return '';
    if (t.days > 0) return `${t.days}d ${t.hours}h`;
    if (t.hours > 0) return `${t.hours}h ${t.minutes}m`;
    if (t.minutes > 0) return `${t.minutes}m`;
    return `${t.seconds}s`;
  };

  // Offer badge: custom label or computed discount %
  const getOfferBadge = () => {
    if (product.offer_label) return product.offer_label;
    if (product.price && product.offer_price && product.price > product.offer_price) {
      const pct = Math.round((1 - product.offer_price / product.price) * 100);
      return `-${pct}%`;
    }
    return 'OFERTA';
  };

  const formatPrice = (p) => `$${Number(p).toLocaleString('es-MX', { minimumFractionDigits: p % 1 !== 0 ? 2 : 0 })}`;

  // Pick a colorful emoji based on keywords in the title/description
  const getProductEmoji = () => {
    const text = `${product.title || ''} ${product.description || ''}`.toLowerCase();
    const rules = [
      { emoji: '📱', kw: ['celular', 'smartphone', 'iphone', 'telefono', 'teléfono', 'móvil', 'movil', 'android', 'samsung', 'xiaomi', 'motorola', 'moto g', 'funda', 'case', 'cargador', 'mica'] },
      { emoji: '💻', kw: ['laptop', 'notebook', 'portatil', 'portátil', 'macbook', 'computadora', 'pc', 'ordenador'] },
      { emoji: '📺', kw: ['tv', 'televisor', 'television', 'televisión', 'pantalla', 'smart tv', 'monitor'] },
      { emoji: '🎧', kw: ['audifonos', 'audífonos', 'auriculares', 'headphones', 'earbuds', 'airpods', 'bocina', 'parlante', 'speaker', 'soundcore'] },
      { emoji: '⌚', kw: ['reloj', 'watch', 'smartwatch', 'pulsera'] },
      { emoji: '📷', kw: ['camara', 'cámara', 'camera', 'gopro', 'fotografia', 'fotografía', 'lente'] },
      { emoji: '🎮', kw: ['consola', 'gamer', 'gaming', 'playstation', 'xbox', 'nintendo', 'videojuego', 'joystick', 'control'] },
      { emoji: '👟', kw: ['tenis', 'zapatillas', 'zapatos', 'calzado', 'sneakers', 'botas', 'sandalias'] },
      { emoji: '👕', kw: ['ropa', 'camisa', 'playera', 'camiseta', 'pantalon', 'pantalón', 'vestido', 'chamarra', 'sudadera', 'jeans', 'moda'] },
      { emoji: '💄', kw: ['perfume', 'fragancia', 'colonia', 'belleza', 'maquillaje', 'cosmetico', 'cosmético', 'crema', 'skincare', 'labial'] },
      { emoji: '🍳', kw: ['cocina', 'sarten', 'sartén', 'olla', 'vajilla', 'cafetera', 'licuadora', 'freidora', 'utensilio', 'air fryer'] },
      { emoji: '🛋️', kw: ['hogar', 'mueble', 'sillon', 'sillón', 'sofa', 'sofá', 'cama', 'colchon', 'colchón', 'lampara', 'lámpara', 'decoracion', 'decoración'] },
      { emoji: '🧸', kw: ['bebe', 'bebé', 'niño', 'nino', 'infantil', 'pañal', 'panal', 'juguete'] },
      { emoji: '🏋️', kw: ['gym', 'fitness', 'pesas', 'mancuerna', 'deporte', 'ejercicio', 'proteina', 'proteína'] },
      { emoji: '🚗', kw: ['auto', 'carro', 'coche', 'llanta', 'neumatico', 'neumático', 'motor', 'automotriz', 'moto'] },
      { emoji: '📚', kw: ['libro', 'cuaderno', 'papeleria', 'papelería', 'escolar', 'lectura'] },
      { emoji: '🔧', kw: ['herramienta', 'taladro', 'destornillador', 'ferreteria', 'ferretería'] },
      { emoji: '🐶', kw: ['mascota', 'perro', 'gato', 'alimento para', 'croqueta'] },
      { emoji: '🎁', kw: ['regalo', 'gift', 'set de regalo'] },
    ];
    for (const rule of rules) {
      if (rule.kw.some((k) => text.includes(k))) return rule.emoji;
    }
    return '🛍️';
  };
  const productEmoji = getProductEmoji();

  // Ensure the offer badge is always visible: if the configured discount bg is light/white,
  // fall back to a vivid red so the discount stands out; pick contrasting text.
  const isLightColor = (hex) => {
    if (!hex || typeof hex !== 'string') return true;
    const c = hex.replace('#', '');
    if (c.length !== 6) return true;
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.75;
  };
  const badgeBg = isLightColor(discountBg) ? '#ef4444' : discountBg;
  const badgeText = '#ffffff';

  const handleGoToProduct = async () => {
    try {
      await axios.post(`${API}/coupons/${product.id}/click`);
      onUpdate?.({ clicks: (product.clicks || 0) + 1 });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
    if (product.product_link) {
      window.location.href = product.product_link;
    }
  };

  const handleShare = async () => {
    const siteUrl = window.location.origin;
    let priceText = '';
    if (product.offer_price) {
      priceText = `💰 ${formatPrice(product.offer_price)}`;
      if (product.price && product.price > product.offer_price) {
        priceText += ` (antes ${formatPrice(product.price)})`;
      }
    }
    const message = `🛍️ ¡Mira esta oferta!\n\n🏷️ ${product.title}\n${priceText}\n\n🔗 ${product.product_link || siteUrl}\n\n✨ Más ofertas en: ${siteUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: product.title, text: message });
        toast.success('¡Compartido!');
        axios.post(`${API}/coupons/${product.id}/share`).catch(() => {});
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    axios.post(`${API}/coupons/${product.id}/share`).catch(() => {});
  };

  return (
    <div
      className={`group relative rounded-[20px] overflow-hidden transition-all duration-200 ease-out active:scale-[0.99] sm:hover:-translate-y-0.5 cursor-pointer ${isExpired ? 'opacity-60 grayscale' : ''} ${recommended ? 'ring-2 ring-amber-400' : ''}`}
      style={{ backgroundColor: cardBg, backgroundImage: cardBgImage, border: `1px solid ${cardBorder}`, boxShadow: cardShadow }}
      data-testid={`product-card-${product.id}`}
      onClick={() => setShowDetail(true)}
    >
      <div className="flex flex-col">
        {recommended && (
          <div
            className="w-full py-1 text-center text-[10px] sm:text-xs font-black text-white tracking-wide"
            style={{ backgroundColor: '#f59e0b' }}
            data-testid={`product-recommended-${product.id}`}
          >
            ⭐ RECOMENDADO
          </div>
        )}
        {/* Image + floating badges */}
        <div className="relative">
          {product.image_url ? (
            <div className={`w-full h-32 sm:h-36 flex items-center justify-center overflow-hidden ${darkMode ? 'bg-slate-900' : 'bg-white'}`}>
              <img
                src={getImageUrl ? getImageUrl(product.image_url) : product.image_url}
                alt={product.title}
                width={200}
                height={144}
                loading="lazy"
                decoding="async"
                className="max-h-full max-w-full object-contain p-2"
              />
            </div>
          ) : (
            <div
              className={`w-full h-32 sm:h-36 flex items-center justify-center overflow-hidden ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}
              data-testid={`product-image-placeholder-${product.id}`}
            >
              <span className="text-5xl sm:text-6xl leading-none select-none" role="img" aria-label="producto">
                {productEmoji}
              </span>
            </div>
          )}

          {/* Offer badge pill */}
          <span
            className="absolute top-2 left-2 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-black shadow-sm"
            style={{ backgroundColor: badgeBg, color: badgeText }}
          >
            {getOfferBadge()}
          </span>

          {/* Expira badge */}
          {!isExpired && timeRemaining && (
            <span
              data-testid={`product-time-${product.id}`}
              className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] sm:text-[10px] font-bold shadow-sm backdrop-blur-sm ${
                isExpiringSoon ? 'bg-red-500 text-white animate-pulse' : (darkMode ? 'bg-slate-800/85 text-slate-200' : 'bg-white/85 text-slate-600')
              }`}
            >
              <Clock className="w-2.5 h-2.5 flex-shrink-0" />
              {formatCompactTime(timeRemaining)}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="px-3 py-3 flex flex-col gap-2">
          <p className="text-xs sm:text-sm font-bold leading-tight line-clamp-2" style={{ color: textColor }}>
            {product.title}
          </p>

          {product.description && (
            <p className="text-[11px] sm:text-xs font-medium line-clamp-2 leading-snug" style={{ color: textColor, opacity: 0.7 }}>
              {product.description}
            </p>
          )}

          {/* Prices */}
          {(product.offer_price || product.price) && (
            <div className="flex items-baseline gap-2" data-testid={`product-price-${product.id}`}>
              {product.offer_price ? (
                <>
                  <span className="font-fredoka text-xl sm:text-2xl font-black" style={{ color: textColor }}>
                    {formatPrice(product.offer_price)}
                  </span>
                  {product.price && product.price > product.offer_price && (
                    <span className="text-[11px] sm:text-xs font-bold line-through" style={{ color: textColor, opacity: 0.45 }}>
                      {formatPrice(product.price)}
                    </span>
                  )}
                </>
              ) : (
                <span className="font-fredoka text-xl sm:text-2xl font-black" style={{ color: textColor }}>
                  {formatPrice(product.price)}
                </span>
              )}
            </div>
          )}

          {/* CTA */}
          <button
            onClick={(e) => { e.stopPropagation(); handleGoToProduct(); }}
            disabled={isExpired}
            data-testid={`product-cta-${product.id}`}
            className={`w-full h-11 rounded-xl font-extrabold text-xs sm:text-sm transition-transform duration-200 flex items-center justify-center gap-1.5 shadow-[0_4px_14px_rgba(0,0,0,0.10)] ${isExpired ? 'opacity-50 cursor-not-allowed' : 'active:scale-[0.98] sm:hover:scale-[1.01] sm:hover:-translate-y-0.5'}`}
            style={{ backgroundColor: buttonBg, color: buttonText, backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.28), rgba(0,0,0,0.03))' }}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            <span>{settings.product_button_text || 'Ver Oferta'}</span>
          </button>

          {/* Bottom row */}
          <div className={`flex items-center justify-between pt-2 border-t ${darkMode ? 'border-slate-700/60' : 'border-slate-100'}`}>
            <button
              onClick={(e) => { e.stopPropagation(); handleShare(); }}
              className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-all active:scale-95 ${darkMode ? 'border-slate-700 hover:bg-slate-700/50' : 'border-slate-200 hover:bg-slate-50'}`}
              title="Compartir"
              data-testid={`product-share-${product.id}`}
            >
              <Share2 className="w-3.5 h-3.5" style={{ color: textColor }} />
            </button>
            <div className="flex items-center gap-2 text-[10px] sm:text-xs font-bold" style={{ color: textColor, opacity: 0.7 }}>
              {settings.show_copies_count && (
                <span>{product.clicks || 0} visitas</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {showDetail && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
          onClick={(e) => { e.stopPropagation(); setShowDetail(false); }}
          data-testid={`product-detail-overlay-${product.id}`}
        >
          <div
            className="relative w-full max-w-sm max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200"
            style={{ backgroundColor: cardBg, backgroundImage: cardBgImage }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`product-detail-modal-${product.id}`}
          >
            <button
              onClick={() => setShowDetail(false)}
              className="absolute top-2.5 right-2.5 z-10 w-8 h-8 rounded-full flex items-center justify-center bg-black/40 text-white hover:bg-black/60 transition-colors active:scale-95"
              title="Cerrar"
              data-testid={`product-detail-close-${product.id}`}
            >
              <X className="w-4 h-4" />
            </button>

            {/* Image (compact) */}
            {product.image_url ? (
              <div className={`w-full h-36 flex items-center justify-center overflow-hidden rounded-t-2xl ${darkMode ? 'bg-slate-900' : 'bg-white'}`}>
                <img
                  src={getImageUrl ? getImageUrl(product.image_url) : product.image_url}
                  alt={product.title}
                  decoding="async"
                  className="max-h-full max-w-full object-contain p-2"
                />
              </div>
            ) : (
              <div className={`w-full h-32 flex items-center justify-center rounded-t-2xl ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                <span className="text-6xl leading-none select-none" role="img" aria-label="producto">{productEmoji}</span>
              </div>
            )}

            <div className="p-4 flex flex-col gap-2.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-black shadow-sm" style={{ backgroundColor: badgeBg, color: badgeText }}>
                  {getOfferBadge()}
                </span>
                {!isExpired && timeRemaining && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${isExpiringSoon ? 'bg-red-500 text-white' : (darkMode ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-600')}`}>
                    <Clock className="w-3 h-3" /> {formatCompactTime(timeRemaining)}
                  </span>
                )}
              </div>

              <h3 className="text-base font-black leading-tight" style={{ color: textColor }}>
                {product.title}
              </h3>

              {product.description && (
                <p className="text-[13px] font-medium leading-relaxed whitespace-pre-line" style={{ color: textColor, opacity: 0.8 }} data-testid={`product-detail-description-${product.id}`}>
                  {product.description}
                </p>
              )}

              {(product.offer_price || product.price) && (
                <div className="flex items-baseline gap-2">
                  {product.offer_price ? (
                    <>
                      <span className="font-fredoka text-2xl font-black" style={{ color: textColor }}>{formatPrice(product.offer_price)}</span>
                      {product.price && product.price > product.offer_price && (
                        <span className="text-xs font-bold line-through" style={{ color: textColor, opacity: 0.45 }}>{formatPrice(product.price)}</span>
                      )}
                    </>
                  ) : (
                    <span className="font-fredoka text-2xl font-black" style={{ color: textColor }}>{formatPrice(product.price)}</span>
                  )}
                </div>
              )}

              <button
                onClick={(e) => { e.stopPropagation(); handleGoToProduct(); }}
                disabled={isExpired}
                data-testid={`product-detail-cta-${product.id}`}
                className={`w-full h-11 rounded-xl font-extrabold text-sm flex items-center justify-center gap-2 transition-transform duration-200 ${isExpired ? 'opacity-50 cursor-not-allowed' : 'active:scale-[0.98] sm:hover:scale-[1.01]'}`}
                style={{ backgroundColor: buttonBg, color: buttonText, backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.28), rgba(0,0,0,0.03))' }}
              >
                <ExternalLink className="w-4 h-4" />
                <span>{settings.product_button_text || 'Ver Oferta'}</span>
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); handleShare(); }}
                className={`w-full h-10 rounded-xl font-bold text-sm flex items-center justify-center gap-2 border transition-all active:scale-[0.98] ${darkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-700/50' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
              >
                <Share2 className="w-4 h-4" /> Compartir
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProductCard;
